import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Alert,
  Button,
  Card,
  Container,
  Link,
  LinearProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useSettings from '../../../hooks/useSettings';
// components
import Page from '../../../components/Page';
import Label from '../../../components/Label';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
// data
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { siteBase } from 'src/utils/siteUrl';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import {
  HomeSectionRow,
  PRICING_CONFIRM_TEXT,
  PRICING_CONFIRM_TITLE,
  UNCONFIRMED_TEXT,
  applyResult,
  canManageHomeSections,
  errorText,
  isUnconfirmedFailure,
  lastChangeText,
  loadIsCurrent,
  needsConfirm,
  rollbackState,
  rowsFrom,
  saveMessage,
  sectionInfo,
  toggleState,
} from './homeSectionsModel';

// ----------------------------------------------------------------------
// Home page sections (plan H, docs/oct27-work/plans/H.md §4.3): one show/hide
// switch per kayfa.io home page section. The hero always shows and is not
// listed. Show/hide only: there is no order to change (HS-6).
//
// Every toggle is ONE `PATCH /home-sections/:key { visible }`; the server never
// receives the whole list (HS-8). The switch flips at once, is disabled while
// its request runs and rolls back on failure, with the error in an Alert above
// the table (HS-9). A PATCH answer only updates its own row (HS-9b); the full
// list comes from a GET after the last pending write settles, and an older GET
// answer is dropped. A failure without an HTTP answer may still have been saved,
// so that GET then shows the saved state (HS-9c). A failed first load shows an
// error and Retry, never the defaults (HS-10).

type Failure = { text: string; retry: () => void };

const NO_LIST_TEXT = 'The server answered without a usable list of sections.';

export default function HomeSectionsPage() {
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const canManage = canManageHomeSections((user as any)?.roles);

  const [rows, setRows] = useState<HomeSectionRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [confirmKey, setConfirmKey] = useState<string | null>(null);

  // The rows as last rendered, readable from async callbacks without a stale
  // closure. Every change goes through update() so the two never disagree.
  const rowsRef = useRef<HomeSectionRow[] | null>(null);
  const loadSeq = useRef(0);
  const pendingWrites = useRef(0);
  const mounted = useRef(true);

  const update = useCallback((change: (rows: HomeSectionRow[]) => HomeSectionRow[]) => {
    if (!mounted.current || !rowsRef.current) return;
    rowsRef.current = change(rowsRef.current);
    setRows(rowsRef.current);
  }, []);

  const load = useCallback(async () => {
    loadSeq.current += 1;
    const seq = loadSeq.current;
    setLoading(true);
    try {
      const res = await axiosInstance.get(APIS.HOME_SECTIONS.ADMIN);
      if (!mounted.current || !loadIsCurrent(seq, loadSeq.current, pendingWrites.current)) return;
      const next = rowsFrom(res.data?.data ?? res.data);
      if (!next) {
        if (!rowsRef.current) setLoadError(NO_LIST_TEXT);
        else setFailure({ text: `Could not refresh the list: ${NO_LIST_TEXT}`, retry: load });
        return;
      }
      rowsRef.current = next;
      setRows(next);
      setLoadError(null);
    } catch (e) {
      if (!mounted.current || seq !== loadSeq.current) return;
      const text = errorText(e);
      // Without a list there is nothing true to show: error + Retry, no switches.
      if (!rowsRef.current) setLoadError(text);
      else setFailure({ text: `Could not load the saved state: ${text}`, retry: load });
    } finally {
      if (mounted.current && seq === loadSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (canManage) load();
    return () => {
      mounted.current = false;
    };
  }, [canManage, load]);

  const write = async (key: string, nextVisible: boolean) => {
    const current = rowsRef.current?.find((r) => r.key === key);
    if (!current || current.pending) return;
    const previous = current.visible;
    setFailure(null);
    update((rs) => toggleState(rs, key, nextVisible));
    pendingWrites.current += 1;
    try {
      const res = await axiosInstance.patch(APIS.HOME_SECTIONS.PATCH(key), {
        visible: nextVisible,
      });
      const data = res.data?.data ?? res.data;
      update((rs) => applyResult(rs, key, data));
      if (mounted.current) enqueueSnackbar(saveMessage(data?.revalidated === true));
    } catch (e) {
      update((rs) => rollbackState(rs, key, previous));
      const unconfirmed = isUnconfirmedFailure(e);
      if (mounted.current) {
        setFailure({
          text: unconfirmed ? `${errorText(e)} ${UNCONFIRMED_TEXT}` : errorText(e),
          retry: () => write(key, nextVisible),
        });
      }
    } finally {
      pendingWrites.current -= 1;
      // One GET once nothing is in flight: other staff's changes, and the
      // saved state after a failure that may have been written (HS-9c).
      if (pendingWrites.current === 0 && mounted.current) load();
    }
  };

  const onSwitch = (key: string, nextVisible: boolean) => {
    if (needsConfirm(key, nextVisible)) setConfirmKey(key);
    else write(key, nextVisible);
  };

  const homeUrl = `${siteBase()}/`;

  return (
    <Page title="Home page sections">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Home page sections"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Marketing' },
            { name: 'Home page sections' },
          ]}
        />

        <Card sx={{ p: 2.5, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Typography variant="body2" color="text.secondary">
              Choose which sections show on the kayfa.io home page. The top banner (hero) always
              shows. Changes reach the site within about a minute.
            </Typography>
            <Link
              href={homeUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}
            >
              Open home page
              <Iconify icon="eva:external-link-fill" sx={{ ml: 0.5, width: 16, height: 16 }} />
            </Link>
          </Stack>
        </Card>

        {!canManage && (
          <Alert severity="error">You do not have access to change home page sections.</Alert>
        )}

        {canManage && !rows && (
          <Card>
            {loadError ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={load} disabled={loading}>
                    Retry
                  </Button>
                }
              >
                Could not load the home page sections: {loadError}
              </Alert>
            ) : (
              <LinearProgress />
            )}
          </Card>
        )}

        {canManage && rows && (
          <>
            {failure && (
              <Alert
                severity="error"
                sx={{ mb: 2, whiteSpace: 'pre-line' }}
                onClose={() => setFailure(null)}
                action={
                  <Button color="inherit" size="small" onClick={failure.retry}>
                    Retry
                  </Button>
                }
              >
                {failure.text}
              </Alert>
            )}

            <Card>
              <TableContainer sx={{ position: 'relative', overflowX: 'auto' }}>
                {loading && (
                  <LinearProgress
                    sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 9 }}
                  />
                )}
                <Table sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Section</TableCell>
                      <TableCell align="center" sx={{ width: 100 }}>
                        Shown
                      </TableCell>
                      <TableCell align="center" sx={{ width: 110 }}>
                        Status
                      </TableCell>
                      <TableCell>Last change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => {
                      const info = sectionInfo(r.key);
                      return (
                        <TableRow key={r.key} hover data-section-key={r.key}>
                          <TableCell>
                            <Typography variant="subtitle2">{info.label}</Typography>
                            {info.labelAr && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="div"
                                dir="rtl"
                                sx={{ textAlign: 'left' }}
                              >
                                {info.labelAr}
                              </Typography>
                            )}
                            {info.note && (
                              <Typography
                                variant="caption"
                                color={info.known ? 'text.secondary' : 'warning.main'}
                                component="div"
                                sx={{ mt: 0.5 }}
                              >
                                {info.note}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              checked={r.visible}
                              disabled={!!r.pending}
                              onChange={(e) => onSwitch(r.key, e.target.checked)}
                              inputProps={{ 'aria-label': `Show ${info.label}` }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Label color={r.visible ? 'success' : 'default'}>
                              {r.visible ? 'Shown' : 'Hidden'}
                            </Label>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {r.pending ? 'Saving…' : lastChangeText(r)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </>
        )}
      </Container>

      <ConfirmationDialog
        isOpen={confirmKey !== null}
        onClose={() => setConfirmKey(null)}
        confirmAction={() => {
          if (confirmKey) write(confirmKey, false);
        }}
        headText={PRICING_CONFIRM_TITLE}
        text={PRICING_CONFIRM_TEXT}
        loading={false}
      />
    </Page>
  );
}
